import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileBarChart, Loader2, Check, Download, FileText, Sparkles, Mail } from "lucide-react";
import MemberEmailComposer from "@/components/MemberEmailComposer";

const TYPES = {
  mensuel: { label: "Mensuel", cls: "bg-blue-100 text-blue-700" },
  regularisation_3mois: { label: "Régularisation 3 mois", cls: "bg-amber-100 text-amber-700" },
  final_cout_reel: { label: "Final coût réel", cls: "bg-emerald-100 text-emerald-700" },
};
const EMPTY = { entreprise_id: "", periode: "", type: "mensuel", montant: 0 };

export default function AppelsContribution() {
  const [items, setItems] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [emailAppel, setEmailAppel] = useState(null); // {id, type, reference, entreprise_id}

  const load = async () => {
    setLoading(true);
    const [a, b] = await Promise.all([api.get("/appels-contribution"), api.get("/entreprises")]);
    setItems(a.data); setEntreprises(b.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/appels-contribution", { ...form, montant: Number(form.montant) });
      toast.success("Appel émis"); setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const payer = async (id) => {
    try { await api.post(`/appels-contribution/${id}/payer`); toast.success("Marqué payé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadPDF = async (id, type = "facture") => {
    try {
      const resp = await api.get(`/appels-contribution/${id}/pdf`, { params: { type }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [aiOpen, setAiOpen] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const completerIA = async (id, type = "facture") => {
    setAiOpen(true); setAiLoading(true); setAiResult(null);
    try {
      const { data } = await api.post("/assistant/completer-appel", { appel_id: id, type });
      setAiResult(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setAiOpen(false); }
    finally { setAiLoading(false); }
  };

  const entLabel = (id) => entreprises.find(e => e.id === id)?.raison_sociale || "—";

  return (
    <div data-testid="appels-page">
      <PageHeader
        title="Appels à contribution"
        subtitle="Mensuels, régularisation à 3 mois et appel final au coût réel."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="appel-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <Plus className="h-4 w-4 mr-2" /> Émettre un appel
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvel appel à contribution</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Entreprise *</Label>
                  <Select value={form.entreprise_id} onValueChange={(v)=>setForm({...form, entreprise_id: v})}>
                    <SelectTrigger data-testid="ap-entreprise"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{entreprises.map(e=><SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={form.type} onValueChange={(v)=>setForm({...form, type: v})}>
                    <SelectTrigger data-testid="ap-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TYPES).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Période *</Label><Input required placeholder="2026-02 ou 2026-T1" value={form.periode} onChange={(e)=>setForm({...form, periode: e.target.value})} data-testid="ap-periode" /></div>
                <div className="space-y-2 col-span-2"><Label>Montant (€) *</Label><Input type="number" step="0.01" required value={form.montant} onChange={(e)=>setForm({...form, montant: e.target.value})} data-testid="ap-montant" /></div>
                <DialogFooter className="col-span-2"><Button type="submit" data-testid="ap-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Émettre</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div> :
       items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <FileBarChart className="h-10 w-10 mx-auto text-slate-300" /><p className="mt-3 text-slate-500">Aucun appel émis.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Période</th><th className="px-4 py-3">Entreprise</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">Montant</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3 text-right">Action</th></tr></thead>
            <tbody>{items.map(a => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{a.periode}</td>
                <td className="px-4 py-3 text-slate-700">{entLabel(a.entreprise_id)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${TYPES[a.type]?.cls || ""}`}>{TYPES[a.type]?.label || a.type}</span></td>
                <td className="px-4 py-3 text-right font-semibold">{Number(a.montant).toLocaleString("fr-FR")} €</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${a.statut === "paye" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.statut}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1 flex-wrap">
                    <Button size="sm" variant="outline" onClick={()=>downloadPDF(a.id, "devis")} data-testid={`ap-devis-${a.id}`} title="Devis prévisionnel"><FileText className="h-3 w-3 mr-1" />Devis</Button>
                    <Button size="sm" variant="outline" onClick={()=>downloadPDF(a.id, "acompte")} data-testid={`ap-acompte-${a.id}`} title="Acompte / provision"><FileText className="h-3 w-3 mr-1" />Acompte</Button>
                    <Button size="sm" variant="outline" onClick={()=>downloadPDF(a.id, "facture")} data-testid={`ap-facture-${a.id}`} title="Facture officielle"><Download className="h-3 w-3 mr-1" />Facture</Button>
                    <Button size="sm" variant="outline" onClick={()=>completerIA(a.id, "facture")} data-testid={`ap-ia-${a.id}`} title="Compléter par IA" className="border-[#C5A059] text-[#C5A059] hover:bg-[#FDF9F1]"><Sparkles className="h-3 w-3 mr-1" />IA</Button>
                    <Button size="sm" variant="outline" onClick={()=>setEmailAppel({ id: a.id, type: a.type === "adhesion" ? "adhesion" : "facture", reference: a.reference || a.id, entreprise_id: a.entreprise_id })} data-testid={`ap-email-${a.id}`} title="Envoyer au membre" className="border-blue-300 text-blue-700 hover:bg-blue-50"><Mail className="h-3 w-3 mr-1" />Envoyer</Button>
                    {a.statut !== "paye" && <Button size="sm" variant="outline" onClick={()=>payer(a.id)} data-testid={`ap-payer-${a.id}`}><Check className="h-3 w-3 mr-1" />Payé</Button>}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Compléter par IA — Claude Sonnet 4.5</DialogTitle></DialogHeader>
          {aiLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
          ) : aiResult ? (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                Pré-remplissage pour <b>{aiResult.entreprise}</b> · type <b>{aiResult.type}</b>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-3 py-2">Élément</th><th className="px-3 py-2">Détail</th><th className="px-3 py-2 text-right">Montant</th></tr></thead>
                  <tbody>{(aiResult.charges || []).map((c, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-3 py-2 font-medium">{c.element}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{c.detail}</td>
                      <td className="px-3 py-2 text-right font-medium">{Number(c.montant).toLocaleString("fr-FR")} €</td>
                    </tr>
                  ))}</tbody>
                  <tfoot><tr className="bg-[#FDF9F1]"><td colSpan={2} className="px-3 py-2 font-bold">TOTAL COÛT RÉEL</td><td className="px-3 py-2 text-right font-bold text-[#A88846]">{Number(aiResult.total).toLocaleString("fr-FR")} €</td></tr></tfoot>
                </table>
              </div>
              {aiResult.commentaire && (
                <div className="rounded-lg bg-[#FDF9F1] border border-[#C5A059]/30 p-3 text-sm text-slate-700">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A88846] mb-1">Analyse IA</div>
                  {aiResult.commentaire}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={()=>downloadPDF(aiResult.appel_id, aiResult.type)} data-testid="ap-ia-dl">
                  <Download className="h-4 w-4 mr-2" />Télécharger PDF complété
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <MemberEmailComposer
        open={!!emailAppel}
        onOpenChange={(v) => !v && setEmailAppel(null)}
        defaultEntrepriseId={emailAppel?.entreprise_id}
        attachAppel={emailAppel ? { id: emailAppel.id, type: emailAppel.type, reference: emailAppel.reference } : null}
      />
    </div>
  );
}
